import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'
import { unifiedAuth } from '../../api/authClient'
import { useAuthStore } from 'shared/store/authStore'

const schema = z.object({
  email: z.string().email('auth.login.validation.invalidEmail'),
  password: z.string().min(1, 'auth.login.validation.passwordRequired'),
})
const schema2fa = z.object({
  code: z.string().length(6, 'auth.login.validation.codeLength'),
})

type FormData = z.infer<typeof schema>
type Form2FA = z.infer<typeof schema2fa>

export interface UnifiedLoginFormProps {
  onClose?: () => void
  /** When in modal, call this to switch to register (e.g. close login modal and open register modal). */
  onOpenRegister?: () => void
  /** When provided, call after successful login instead of navigating (e.g. stay in payment modal). */
  onSuccess?: () => void
  embedded?: boolean
}

export default function UnifiedLoginForm({ onClose, onOpenRegister, onSuccess, embedded }: UnifiedLoginFormProps) {
  const { t } = useTranslation('portal')
  const [error, setError] = useState<string | null>(null)
  const [step, setStep] = useState<'credentials' | '2fa' | 'email_verify'>('credentials')
  const [tempToken, setTempToken] = useState<string | null>(null)
  const [verificationEmail, setVerificationEmail] = useState('')
  const [otpCode, setOtpCode] = useState('')
  const [otpVerifying, setOtpVerifying] = useState(false)
  const [resendCooldown, setResendCooldown] = useState(0)
  const [savedPassword, setSavedPassword] = useState('')
  const navigate = useNavigate()
  const customer = useAuthStore((s) => s.customer)
  const setCustomerAuth = useAuthStore((s) => s.setCustomerAuth)

  /** Login always redirects to /dashboard. */
  const getRedirect = () => '/dashboard'

  const { register, handleSubmit, formState: { errors } } = useForm<FormData>({
    resolver: zodResolver(schema),
  })
  const { register: register2fa, handleSubmit: handleSubmit2fa, formState: { errors: errors2fa } } = useForm<Form2FA>({
    resolver: zodResolver(schema2fa),
  })

  const applyLoginResponse = (res: {
    access_token?: string
    user_id?: string
    email?: string
    role?: string
  }) => {
    if (!res.access_token || res.user_id == null || res.email == null || res.role == null) return false
    setCustomerAuth(res.access_token, { id: res.user_id, email: res.email, role: res.role })
    if (onSuccess) {
      onSuccess()
      return true
    }
    onClose?.()
    navigate(getRedirect(), { replace: true })
    return true
  }

  const onSubmit = async (data: FormData) => {
    setError(null)
    try {
      const { data: res } = await unifiedAuth.login(data.email, data.password)
      if (res.requires_email_verification) {
        setVerificationEmail((res.email || data.email).trim().toLowerCase())
        setSavedPassword(data.password)
        setStep('email_verify')
        startResendCooldown()
        return
      }
      if (res.requires_2fa && res.temp_token) {
        setTempToken(res.temp_token)
        setStep('2fa')
        return
      }
      applyLoginResponse(res)
    } catch (e: unknown) {
      const res = e && typeof e === 'object' && 'response' in e ? (e.response as { status?: number; data?: { detail?: string } }) : null
      const status = res?.status
      const detail = res?.data?.detail != null ? String(res.data.detail) : undefined
      const msg = (() => {
        if (status === 500 || detail === 'Login failed') return t('auth.login.errorDefault')
        if (detail === 'Invalid email or password') return t('auth.login.errorInvalidCredentials')
        return detail ?? t('auth.login.errorDefault')
      })()
      setError(msg)
    }
  }

  const onVerify2fa = async (data: Form2FA) => {
    if (!tempToken) return
    setError(null)
    try {
      const { data: res } = await unifiedAuth.verify2fa(tempToken, data.code)
      if (!applyLoginResponse(res)) setError(t('auth.login.errorDefault'))
    } catch (e: unknown) {
      const res = e && typeof e === 'object' && 'response' in e ? (e.response as { data?: { detail?: string } }) : null
      const detail = res?.data?.detail != null ? String(res.data.detail) : undefined
      setError(detail === 'Invalid verification code' ? t('auth.login.error2fa') : (detail ?? t('auth.login.error2fa')))
    }
  }

  const startResendCooldown = () => {
    setResendCooldown(60)
    const interval = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) { clearInterval(interval); return 0 }
        return prev - 1
      })
    }, 1000)
  }

  const handleVerifyEmailOtp = async () => {
    setError(null)
    setOtpVerifying(true)
    try {
      await unifiedAuth.verifyEmail(verificationEmail, otpCode)
      // Email verified — now log in automatically
      const { data: res } = await unifiedAuth.login(verificationEmail, savedPassword)
      if (!applyLoginResponse(res)) setError(t('auth.login.errorDefault'))
    } catch (e: unknown) {
      const detail =
        e && typeof e === 'object' && 'response' in e
          ? (e.response as { data?: { detail?: string } })?.data?.detail
          : undefined
      setError(detail ? String(detail) : t('auth.register.otpInvalid'))
    } finally {
      setOtpVerifying(false)
    }
  }

  const handleResendOtp = async () => {
    setError(null)
    try {
      await unifiedAuth.resendOtp(verificationEmail)
      startResendCooldown()
    } catch (e: unknown) {
      const detail =
        e && typeof e === 'object' && 'response' in e
          ? (e.response as { data?: { detail?: string } })?.data?.detail
          : undefined
      setError(detail ? String(detail) : t('auth.login.errorDefault'))
    }
  }

  const isLight = embedded
  const inputClass = isLight
    ? 'w-full rounded-lg bg-white border border-slate-300 px-3 py-2 text-slate-800 placeholder-slate-400 focus:border-blue-500 focus:outline-none'
    : 'w-full rounded-lg bg-slate-800 border border-slate-700 px-3 py-2 text-slate-200 placeholder-slate-500 focus:border-emerald-500 focus:outline-none'
  const labelClass = isLight
    ? 'block text-sm font-medium text-slate-600 mb-1'
    : 'block text-sm font-medium text-slate-400 mb-1'
  const linkClass = isLight ? 'text-blue-600 hover:underline' : 'text-emerald-500 hover:underline'

  const translateMsg = (msg: string | undefined) => {
    if (!msg) return ''
    return t(msg) !== msg ? t(msg) : msg
  }

  // When already logged in: if used in modal (onSuccess), stay there and call onSuccess; otherwise redirect to dashboard
  useEffect(() => {
    if (customer?.token && customer?.user) {
      if (onSuccess) {
        onSuccess()
        return
      }
      onClose?.()
      navigate(getRedirect(), { replace: true })
    }
  }, [customer?.token, customer?.user, onClose, navigate, onSuccess])

  if (customer?.token && customer?.user) {
    return null
  }

  if (step === 'email_verify') {
    return (
      <div
        className={embedded ? undefined : 'w-full max-w-sm rounded-xl bg-slate-900 border border-slate-800 p-6 shadow-xl'}
        data-testid="unified-login-email-verify"
      >
        <h2 className={`text-xl font-semibold mb-2 text-center ${isLight ? 'text-slate-900' : 'text-white'}`}>
          {t('auth.register.otpTitle')}
        </h2>
        <p className={`text-sm mb-4 text-center ${isLight ? 'text-slate-600' : 'text-slate-400'}`}>
          {t('auth.register.otpSentTo')} <strong>{verificationEmail}</strong>
        </p>
        {error && <p className="text-sm text-red-500 mb-3 text-center">{error}</p>}
        <div className="space-y-3">
          <input
            type="text"
            inputMode="numeric"
            maxLength={6}
            value={otpCode}
            onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="000000"
            className={`${inputClass} text-center text-2xl tracking-[0.5em] font-mono`}
            autoFocus
          />
          <button
            type="button"
            onClick={handleVerifyEmailOtp}
            disabled={otpCode.length !== 6 || otpVerifying}
            className={`w-full rounded-lg font-medium py-2 px-4 transition-colors disabled:opacity-50 ${
              isLight ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-emerald-600 hover:bg-emerald-500 text-white'
            }`}
          >
            {otpVerifying ? '...' : t('auth.register.otpVerify')}
          </button>
          <p className={`text-xs text-center ${isLight ? 'text-slate-500' : 'text-slate-500'}`}>
            {resendCooldown > 0 ? (
              <span>{t('auth.register.otpResendIn')} {resendCooldown}s</span>
            ) : (
              <button type="button" onClick={handleResendOtp} className={linkClass}>
                {t('auth.register.otpResend')}
              </button>
            )}
          </p>
          <button
            type="button"
            onClick={() => { setStep('credentials'); setError(null); setOtpCode(''); }}
            className={`w-full rounded-lg border py-2 px-4 text-sm ${isLight ? 'border-slate-300 text-slate-600 hover:bg-slate-50' : 'border-slate-600 text-slate-400 hover:text-slate-200'}`}
          >
            {t('auth.login.backToLogin')}
          </button>
        </div>
      </div>
    )
  }

  if (step === '2fa') {
    return (
      <div className={embedded ? undefined : 'w-full max-w-sm rounded-xl bg-slate-900 border border-slate-800 p-6 shadow-xl'} data-testid="unified-login-2fa">
        <h1 className={`text-xl font-semibold mb-1 ${isLight ? 'text-slate-900' : 'text-white'}`}>{t('auth.login.title2fa')}</h1>
        <p className={`text-sm mb-6 ${isLight ? 'text-slate-600' : 'text-slate-500'}`}>{t('auth.login.subtitle2fa')}</p>
        <form onSubmit={handleSubmit2fa(onVerify2fa)} className="space-y-4">
          <div>
            <label className={labelClass}>{t('auth.login.code')}</label>
            <input
              {...register2fa('code')}
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              className={`${inputClass} text-center text-lg tracking-widest`}
              placeholder="000000"
            />
            {errors2fa.code && <p className="mt-1 text-sm text-red-500">{translateMsg(errors2fa.code.message)}</p>}
          </div>
          {error && <p className="text-sm text-red-500">{error}</p>}
          <button
            type="submit"
            className={`w-full rounded-lg font-medium py-2 px-4 transition-colors ${isLight ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-emerald-600 hover:bg-emerald-500 text-white'}`}
          >
            {t('auth.login.verify')}
          </button>
          <button
            type="button"
            onClick={() => { setStep('credentials'); setTempToken(null); setError(null); }}
            className={`w-full rounded-lg border py-2 px-4 text-sm ${isLight ? 'border-slate-300 text-slate-600 hover:bg-slate-50' : 'border-slate-600 text-slate-400 hover:text-slate-200'}`}
          >
            {t('auth.login.backToLogin')}
          </button>
        </form>
      </div>
    )
  }

  return (
    <div
      className={embedded ? undefined : 'w-full max-w-sm rounded-xl bg-slate-900 border border-slate-800 p-6 shadow-xl'}
      data-testid="unified-login-form"
    >
      {!embedded && (
        <>
          <h1 className="text-xl font-semibold text-white mb-1" data-testid="heading-login">{t('auth.login.title')}</h1>
          <p className="text-slate-500 text-sm mb-6">{t('auth.login.subtitle')}</p>
        </>
      )}
      {embedded && <p className={`text-sm mb-4 ${isLight ? 'text-slate-600' : 'text-slate-500'}`}>{t('auth.login.subtitle')}</p>}
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <label className={labelClass}>{t('auth.login.email')}</label>
          <input
            {...register('email')}
            type="email"
            className={inputClass}
            placeholder={t('auth.login.placeholderEmail')}
          />
          {errors.email && <p className="mt-1 text-sm text-red-500">{translateMsg(errors.email.message)}</p>}
        </div>
        <div>
          <label className={labelClass}>{t('auth.login.password')}</label>
          <input
            {...register('password')}
            type="password"
            className={inputClass}
            placeholder={t('auth.login.placeholderPassword')}
          />
          {errors.password && <p className="mt-1 text-sm text-red-500">{translateMsg(errors.password.message)}</p>}
        </div>
        {error && <p className="text-sm text-red-500">{error}</p>}
        <button
          type="submit"
          className={`w-full rounded-lg font-medium py-2 px-4 transition-colors ${isLight ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-emerald-600 hover:bg-emerald-500 text-white'}`}
        >
          {t('auth.login.signIn')}
        </button>
      </form>
      <p className={`mt-4 text-center text-sm ${isLight ? 'text-slate-600' : 'text-slate-500'}`}>
        {t('auth.login.noAccount')}{' '}
        {onOpenRegister ? (
          <button type="button" onClick={() => { onClose?.(); onOpenRegister(); }} className={linkClass}>
            {t('auth.login.registerLink')}
          </button>
        ) : (
          <Link to="/register" onClick={onClose} className={linkClass}>{t('auth.login.registerLink')}</Link>
        )}
      </p>
    </div>
  )
}
